import { useModal } from '@ebay/nice-modal-react'
import clsx from 'clsx'
import { useState } from 'react'
import { LoaderIcon } from 'react-hot-toast'
import { BsSearch } from 'react-icons/bs'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useInfiniteQuery, useMutation } from 'react-query'

import { privateRequest } from 'config/axios.config'
import queryClient from 'config/queyClient.config'
import { dateFormatter } from 'utils'
import { errorHandler } from 'utils/errorHandler'
import useDebounce from 'utils/useDebounce'

import NoData from 'components/NoData'
import Button from 'components/form/button'
import Input from 'components/form/input'
import addUpdateSurgery from 'features/surgery/addUpdateSurgery.modal'

interface DeletePayload {
    id: string
}

export default function SurgeryPage() {
    const addUpdateSurgeryModal = useModal(addUpdateSurgery)
    const [search, setSearch] = useState('')
    const debouncedSearchTerm = useDebounce(search, 500)

    const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery<
        SurgeryResponse,
        Error
    >(
        ['surgery', debouncedSearchTerm],
        async ({ pageParam = 1 }) => {
            try {
                const res = await privateRequest.get(
                    `admin/surgery?page=${pageParam}&query=${debouncedSearchTerm}`
                )
                return res.data.data
            } catch (error) {
                errorHandler(error)
            }
        },
        { getNextPageParam: (lastPage) => lastPage.nextPage }
    )

    const deleteMutation = useMutation<{ message: string }, Error, DeletePayload>(
        async ({ id }) => {
            try {
                const res = await privateRequest.delete(`admin/surgery/${id}`)
                return res.data
            } catch (error) {
                errorHandler(error)
                throw error
            }
        },
        {
            onSuccess: () => queryClient.invalidateQueries('surgery'),
        }
    )

    const surgeryList = data?.pages?.flatMap((page) => page.docs) ?? []

    return (
        <div className="card">
            <div className="flex flex-wrap gap-4 mb-10">
                <Button
                    icon="add"
                    size="md"
                    variant="outlined"
                    onClick={() =>
                        addUpdateSurgeryModal.show({ title: 'Add New Surgery' })
                    }
                >
                    Add New
                </Button>

                <Input
                    variant="outlined"
                    placeholder="Search"
                    size="md"
                    className="md:!w-[230px] ml-auto"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    afterFix={<BsSearch className="mr-3 text-gray" size={20} />}
                />
            </div>

            <div
                id="scrollableDiv"
                className="h-[calc(100vh-340px)] md:h-[calc(100vh-325px)] overflow-y-auto"
            >
                <InfiniteScroll
                    dataLength={surgeryList.length}
                    next={fetchNextPage}
                    hasMore={!!hasNextPage}
                    scrollableTarget="scrollableDiv"
                    loader={
                        <div className="flex gap-2 justify-center items-center">
                            <LoaderIcon />
                            Loading...
                        </div>
                    }
                >
                    <table className={clsx({ 'blur-sm animate-pulse': isLoading })}>
                        <thead>
                            <tr>
                                <td>Created date</td>
                                <td>Name</td>
                                <td>Action</td>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading &&
                                Array(10)
                                    .fill(0)
                                    .map((_, i) => (
                                        <tr key={i}>
                                            <td>Day Month, Year</td>
                                            <td>Surgery</td>
                                            <td />
                                        </tr>
                                    ))}

                            {surgeryList.map((row) => (
                                <tr key={row._id}>
                                    <td className="whitespace-nowrap">{dateFormatter(row.createdAt)}</td>
                                    <td>{row.name}</td>
                                    <td>
                                        <div className="inline-flex gap-2">
                                            <Button
                                                size="sm"
                                                onClick={() =>
                                                    addUpdateSurgeryModal.show({
                                                        surgery: row,
                                                        title: 'Update Surgery',
                                                    })
                                                }
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="sm"
                                                color="danger"
                                                onClick={() => deleteMutation.mutate({ id: row._id })}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </InfiniteScroll>

                {!surgeryList?.length && !isLoading && <NoData />}
            </div>
        </div>
    )
}
